import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, Loader2 } from "lucide-react";

import { PersonalizacionTab } from "@/components/settings/PersonalizacionTab";
import { PagosTab } from "@/components/settings/PagosTab";
import { SubscriptionTab } from "@/components/settings/SubscriptionTab";
import { IntegracionesTab } from "@/components/settings/IntegracionesTab";
import { ReceiptConfigTab } from "@/components/settings/ReceiptConfigTab";
import { PrintStationsManager } from "@/components/settings/PrintStationsManager";
import { EnviosTab } from "@/components/settings/EnviosTab";
import { HorariosTab } from "@/components/settings/HorariosTab";
import { DominioTab } from "@/components/settings/DominioTab";
import { APIKeysTab } from "@/components/settings/APIKeysTab";
import { SecurityTab } from "@/components/settings/SecurityTab";
import { KaisoPayTab } from "@/components/settings/KaisoPayTab";
import { SystemTab } from "@/components/settings/SystemTab";
import { AIUsageTab } from "@/components/settings/AIUsageTab";
import { OperacoesTab } from "@/components/settings/OperacoesTab";
import { FiscalTab } from "@/components/settings/FiscalTab";

export default function SettingsPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [contactForm, setContactForm] = useState({});
  const [savingContact, setSavingContact] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  const handleGeocode = async () => {
    setGeocoding(true);
    try {
      await authAxios.post("/restaurant/geocode");
      toast.success("Coordenadas guardadas!");
      fetchRestaurant();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível geocodificar. Verifique o endereço.");
    }
    setGeocoding(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchRestaurant = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/restaurant");
      setRestaurant(res.data);
      setContactForm({
        name: res.data?.name || "",
        postal_code: res.data?.postal_code || "",
        street: res.data?.street || res.data?.address || "",
        street_number: res.data?.street_number || "",
        complement: res.data?.complement || "",
        district: res.data?.district || "",
        city: res.data?.city || "",
        country: res.data?.country || "ES",
        currency: res.data?.currency || "EUR",
        locale: res.data?.locale || "es-ES",
        fiscal_id: res.data?.fiscal_id || res.data?.cif || "",
        phone: res.data?.phone || "",
        cif: res.data?.cif || "",
        email: res.data?.email || "",
        dine_in: res.data?.dine_in !== false,
        google_review_url: res.data?.google_review_url || "",
        instagram_url: res.data?.instagram_url || "",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error cargando datos");
    }
    setLoading(false);
  }, []);

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await authAxios.patch("/restaurant", {
        name: contactForm.name,
        postal_code: contactForm.postal_code,
        street: contactForm.street,
        address: contactForm.street,
        street_number: contactForm.street_number,
        complement: contactForm.complement,
        district: contactForm.district,
        city: contactForm.city,
        country: contactForm.country,
        currency: contactForm.currency,
        locale: contactForm.locale,
        fiscal_id: contactForm.fiscal_id,
        phone: contactForm.phone,
        cif: contactForm.cif,
        email: contactForm.email,
        dine_in: contactForm.dine_in,
        google_review_url: contactForm.google_review_url,
        instagram_url: contactForm.instagram_url,
      });
      toast.success("Datos actualizados correctamente");
      fetchRestaurant();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar datos");
    }
    setSavingContact(false);
  };

  useEffect(() => { fetchRestaurant(); }, [fetchRestaurant]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setCheckingPayment(true);
      const checkStatus = async () => {
        try {
          const res = await authAxiosRef.current.get(`/subscription/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            toast.success("Pago completado! Tu suscripcion esta activa.");
            fetchRestaurant();
          }
        } catch (err) { console.error(err); }
        setCheckingPayment(false);
      };
      checkStatus();
    }
  }, [searchParams, fetchRestaurant]);

  const handleSubscribe = async (plan) => {
    try {
      // billing_currency is resolved server-side from restaurant.billing_currency — not passed here.
      const res = await authAxios.post(`/subscription/checkout?plan=${plan}`);
      window.location.href = res.data.url;
    } catch { toast.error("Error iniciando pago"); }
  };

  const [plans, setPlans] = useState({});
  const [plansCurrencySymbol, setPlansCurrencySymbol] = useState("€");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!restaurant) return;
    // billing_currency drives plan display — falls back to EUR when no price_ids configured.
    const billingCurrency = restaurant?.billing_currency || "EUR";
    authAxiosRef.current.get(`/subscription/plans?billing_currency=${billingCurrency}`)
      .then(r => {
        setPlans(r.data.plans || r.data);
        setPlansCurrencySymbol(r.data.currency_symbol || "€");
      })
      .catch(() => {});
  }, [restaurant]);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold uppercase tracking-tight">Configuracion</h1>
          <p className="text-muted-foreground mt-1">Ajustes de tu restaurante</p>
        </div>

        {loading ? <LoadingScreen /> : (
          <Tabs defaultValue={searchParams.get("tab") || "contacto"}>
            <TabsList className="bg-secondary flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="contacto">Contacto</TabsTrigger>
              <TabsTrigger value="horarios">Horarios</TabsTrigger>
              <TabsTrigger value="operacoes" data-testid="tab-operacoes">Operacoes</TabsTrigger>
              <TabsTrigger value="envios">Envios</TabsTrigger>
              <TabsTrigger value="pagos">Pagos</TabsTrigger>
              <TabsTrigger value="kaisopay">KaisoPay</TabsTrigger>
              <TabsTrigger value="personalizacion">Personalizacion</TabsTrigger>
              <TabsTrigger value="integraciones">Integraciones</TabsTrigger>
              <TabsTrigger value="impresion">Impresion</TabsTrigger>
              <TabsTrigger value="subscription">Suscripcion</TabsTrigger>
              <TabsTrigger value="api_keys">Claves API</TabsTrigger>
              <TabsTrigger value="dominio">Dominio</TabsTrigger>
              <TabsTrigger value="seguranca" data-testid="tab-seguranca">Seguranca</TabsTrigger>
              <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
            </TabsList>

            {/* CONTACTO */}
            <TabsContent value="contacto" className="mt-6 space-y-6">
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="uppercase">Datos del Restaurante</CardTitle>
                  <CardDescription>La direccion es utilizada para calcular el envio hasta el cliente.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input value={contactForm.name} onChange={e => setContactForm(f => ({...f, name: e.target.value}))} className="bg-secondary border-transparent" placeholder="Nombre del restaurante" data-testid="contact-name" />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Codigo postal</Label><Input value={contactForm.postal_code} onChange={e => setContactForm(f => ({...f, postal_code: e.target.value}))} className="bg-secondary border-transparent" placeholder="14010" data-testid="contact-postal" /></div>
                    <div className="space-y-2"><Label>Calle</Label><Input value={contactForm.street} onChange={e => setContactForm(f => ({...f, street: e.target.value}))} className="bg-secondary border-transparent" placeholder="Avenida Barcelona" data-testid="contact-street" /></div>
                    <div className="space-y-2"><Label>Numero</Label><Input value={contactForm.street_number} onChange={e => setContactForm(f => ({...f, street_number: e.target.value}))} className="bg-secondary border-transparent" placeholder="19" data-testid="contact-number" /></div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="space-y-2"><Label>Complemento</Label><Input value={contactForm.complement} onChange={e => setContactForm(f => ({...f, complement: e.target.value}))} className="bg-secondary border-transparent" placeholder="Levante" data-testid="contact-complement" /></div>
                    <div className="space-y-2"><Label>Distrito</Label><Input value={contactForm.district} onChange={e => setContactForm(f => ({...f, district: e.target.value}))} className="bg-secondary border-transparent" placeholder="Cordoba Capital" data-testid="contact-district" /></div>
                    <div className="space-y-2"><Label>Ciudad</Label><Input value={contactForm.city} onChange={e => setContactForm(f => ({...f, city: e.target.value}))} className="bg-secondary border-transparent" placeholder="Cordoba" data-testid="contact-city" /></div>
                    <div className="space-y-2">
                      <Label>Pais</Label>
                      <Select value={contactForm.country} onValueChange={v => {
                        const localeMap = { ES: "es-ES", PT: "pt-PT", BR: "pt-BR", US: "en-US", GB: "en-GB" };
                        setContactForm(f => ({...f, country: v, locale: localeMap[v] || f.locale}));
                      }}>
                        <SelectTrigger className="bg-secondary border-transparent" data-testid="contact-country">
                          <SelectValue placeholder="Seleccionar pais" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ES">Espanha</SelectItem>
                          <SelectItem value="PT">Portugal</SelectItem>
                          <SelectItem value="BR">Brasil</SelectItem>
                          <SelectItem value="US">Estados Unidos</SelectItem>
                          <SelectItem value="GB">Reino Unido</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-bold text-sm mb-3 text-gray-700">Configuracion Regional</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Moneda</Label>
                        <Select value={contactForm.currency} onValueChange={v => setContactForm(f => ({...f, currency: v}))}>
                          <SelectTrigger className="bg-secondary border-transparent" data-testid="contact-currency">
                            <SelectValue placeholder="Seleccionar moneda" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">Euro (EUR €)</SelectItem>
                            <SelectItem value="BRL">Real Brasileiro (BRL R$)</SelectItem>
                            <SelectItem value="USD">US Dollar (USD $)</SelectItem>
                            <SelectItem value="GBP">British Pound (GBP £)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Idioma/Locale</Label>
                        <Select value={contactForm.locale} onValueChange={v => setContactForm(f => ({...f, locale: v}))}>
                          <SelectTrigger className="bg-secondary border-transparent" data-testid="contact-locale">
                            <SelectValue placeholder="Seleccionar idioma" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="es-ES">Espanol (Espana)</SelectItem>
                            <SelectItem value="pt-PT">Portugues (Portugal)</SelectItem>
                            <SelectItem value="pt-BR">Portugues (Brasil)</SelectItem>
                            <SelectItem value="en-US">English (US)</SelectItem>
                            <SelectItem value="en-GB">English (UK)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{contactForm.country === "BR" ? "CPF/CNPJ" : contactForm.country === "PT" ? "NIF" : "NIF/CIF"}</Label>
                        <Input 
                          value={contactForm.fiscal_id} 
                          onChange={e => setContactForm(f => ({...f, fiscal_id: e.target.value}))} 
                          className="bg-secondary border-transparent" 
                          placeholder={contactForm.country === "BR" ? "000.000.000-00" : contactForm.country === "PT" ? "123456789" : "B12345678"} 
                          data-testid="contact-fiscal-id" 
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">La moneda y el idioma se aplican al menu publico y a los recibos.</p>
                  </div>
                  <div className="flex items-center gap-3 py-2"><Switch checked={contactForm.dine_in} onCheckedChange={v => setContactForm(f => ({...f, dine_in: v}))} data-testid="contact-dinein" /><span className="font-medium">Atiende en el local</span></div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Telefono</Label>
                      <Input value={contactForm.phone} onChange={e => setContactForm(f => ({...f, phone: e.target.value}))} className="bg-secondary border-transparent" placeholder="673 036 835" data-testid="contact-phone" />
                      <p className="text-xs text-muted-foreground">Llene el telefono para que los clientes puedan entrar en contacto.</p>
                    </div>
                    <div className="space-y-2"><Label>CIF</Label><Input value={contactForm.cif} onChange={e => setContactForm(f => ({...f, cif: e.target.value}))} className="bg-secondary border-transparent" placeholder="B22473664" data-testid="contact-cif" /></div>
                    <div className="space-y-2"><Label>Email</Label><Input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({...f, email: e.target.value}))} className="bg-secondary border-transparent" placeholder="email@ejemplo.com" data-testid="contact-email" /></div>
                  </div>
                  <div className="space-y-2"><Label>ID del Restaurante (para QR)</Label><Input value={restaurant?.id || ""} className="bg-secondary border-transparent font-mono text-sm" readOnly /></div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-bold text-sm mb-3 text-gray-700">Redes Sociales y Evaluaciones</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Link de Evaluacion Google</Label>
                        <Input value={contactForm.google_review_url} onChange={e => setContactForm(f => ({...f, google_review_url: e.target.value}))} className="bg-secondary border-transparent" placeholder="https://g.page/r/..." data-testid="google-review-url-input" />
                        <p className="text-xs text-muted-foreground">Se envia a los clientes 24h despues de la entrega</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Instagram</Label>
                        <Input value={contactForm.instagram_url} onChange={e => setContactForm(f => ({...f, instagram_url: e.target.value}))} className="bg-secondary border-transparent" placeholder="https://instagram.com/restaurante" data-testid="instagram-url-input" />
                        <p className="text-xs text-muted-foreground">Se pide al cliente que siga y publique</p>
                      </div>
                    </div>
                  </div>
                  
                  <Button className="glow-orange" onClick={saveContact} disabled={savingContact} data-testid="save-contact-btn">
                    {savingContact ? "Guardando..." : "Actualizar Tienda"}
                  </Button>
                  {restaurant?.lat ? (
                    <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> Coordenadas do restaurante configuradas ({Number(restaurant.lat).toFixed(4)}, {Number(restaurant.lng).toFixed(4)})
                    </p>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-2">
                      <MapPin className="w-4 h-4 text-amber-600 shrink-0" />
                      <span className="text-amber-700 text-xs flex-1">
                        Usamos a localização do restaurante para melhorar as sugestões de endereço de entrega.
                      </span>
                      <Button size="sm" variant="outline" onClick={handleGeocode} disabled={geocoding} data-testid="geocode-btn">
                        {geocoding ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <MapPin className="w-3 h-3 mr-1" />}
                        {geocoding ? "Buscando..." : "Buscar coordenadas"}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card className="border-red-500/50">
                <CardHeader><CardTitle className="text-red-500">Zona de peligro</CardTitle></CardHeader>
                <CardContent className="flex gap-3">
                  <Button variant="outline" className="text-red-500 border-red-500/50 hover:bg-red-500/10">Borar Logo</Button>
                  <Button variant="outline" className="text-red-500 border-red-500/50 hover:bg-red-500/10">Borrar Tienda</Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* HORARIOS */}
            <TabsContent value="horarios" className="mt-6 space-y-6">
              <HorariosTab authAxios={authAxios} restaurant={restaurant} onUpdate={fetchRestaurant} />
            </TabsContent>

            {/* OPERACOES */}
            <TabsContent value="operacoes" className="mt-6 space-y-6">
              <OperacoesTab authAxios={authAxios} restaurant={restaurant} onUpdate={fetchRestaurant} />
            </TabsContent>

            <TabsContent value="envios" className="mt-6 space-y-6">
              <EnviosTab authAxios={authAxios} restaurant={restaurant} onUpdate={fetchRestaurant} />
            </TabsContent>

            <TabsContent value="pagos" className="mt-6 space-y-6">
              <PagosTab authAxios={authAxios} restaurant={restaurant} onUpdate={fetchRestaurant} />
            </TabsContent>

            <TabsContent value="personalizacion" className="mt-6">
              <PersonalizacionTab authAxios={authAxios} restaurant={restaurant} />
            </TabsContent>

            <TabsContent value="integraciones" className="mt-6 space-y-6">
              <IntegracionesTab authAxios={authAxios} restaurant={restaurant} onUpdate={fetchRestaurant} />
            </TabsContent>

            <TabsContent value="impresion" className="mt-6 space-y-6">
              <ReceiptConfigTab authAxios={authAxios} restaurant={restaurant} onUpdate={fetchRestaurant} />
              <PrintStationsManager authAxios={authAxios} restaurant={restaurant} onUpdate={fetchRestaurant} />
            </TabsContent>

            <TabsContent value="subscription" className="mt-6 space-y-6">
              <SubscriptionTab authAxios={authAxios} restaurant={restaurant} checkingPayment={checkingPayment} handleSubscribe={handleSubscribe} plans={plans} plansCurrencySymbol={plansCurrencySymbol} fetchRestaurant={fetchRestaurant} />
            </TabsContent>
            <TabsContent value="api_keys" className="mt-6 space-y-6">
              <APIKeysTab authAxios={authAxios} restaurant={restaurant} onRefresh={fetchRestaurant} />
            </TabsContent>
            <TabsContent value="dominio" className="mt-6 space-y-6">
              <DominioTab restaurant={restaurant} onSave={fetchRestaurant} />
            </TabsContent>
            <TabsContent value="seguranca" className="mt-6 space-y-6">
              <SecurityTab authAxios={authAxios} restaurant={restaurant} />
            </TabsContent>
            <TabsContent value="kaisopay" className="mt-6 space-y-6">
              <KaisoPayTab authAxios={authAxios} />
            </TabsContent>
            <TabsContent value="fiscal" className="mt-6 space-y-6">
              <FiscalTab restaurant={restaurant} authAxios={authAxios} onSaved={fetchRestaurant} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </MainLayout>
  );
}
